import { FC, useEffect, useState } from 'react'
import { Chart, BubbleController, Title, Tooltip } from 'chart.js'
import { format } from 'date-fns'
import { ChartProps } from '../../../models/analytics'
import { TBubbleData } from '../../../pages/Analytics'

Chart.register(BubbleController, Title, Tooltip)

const backgroundPosotiveColor = '#EBF4F6'
const hoverColor = '#3A98AB'
const gridColor = '#E2E4F8'
const labelColor = '#9C9EB2'

enum PADDINGS {
  LEFT_PADDING = 'leftPadding',
  RIGTH_PADDING = 'rigthPadding'
}

const countPaddingDependingOfRadiusOfBubble = (
  data: TBubbleData[],
  chartWidth: number,
  paddingType: PADDINGS
) => {
  if(data.length === 0){
    return 0
  }
  const pixelsByOneElement = +chartWidth / data.length
  switch (paddingType) {
    case PADDINGS.LEFT_PADDING: {
      const pixelsOfLeftElementX = data[0].r + 5
      const result = Math.abs(Math.floor(+pixelsOfLeftElementX / +pixelsByOneElement)) + 1
      return result
    }
    case PADDINGS.RIGTH_PADDING: {
      const pixelsOfLeftElementX = data[data.length - 1].r + 5
      const result = Math.abs(Math.floor(+pixelsOfLeftElementX / +pixelsByOneElement)) + 1
      return result
    }
    default:
      return 0
  }
}

const BubbleChart: FC<ChartProps> = ({ social, name, chartData }) => {
  const [myChart, setMyChart] = useState<any>()

  useEffect(() => {
    const ctx = document.getElementById(`${name}_${social}`)!
    const { dataPositive }: { dataPositive: TBubbleData[] } = chartData
    const chart = new Chart(ctx as HTMLCanvasElement, {
      type: 'bubble',
      data: {
        datasets: [
          {
            data: dataPositive,
            backgroundColor: backgroundPosotiveColor,
            borderWidth: 0,
            hoverBackgroundColor: 'transparent',
            hoverBorderColor: hoverColor,
            hoverBorderWidth: 5
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            yAlign: 'bottom',
            position: 'nearest',
            enabled: false,
            external: ({ chart, tooltip }) => {
              // Tooltip Element
              let tooltipEl = chart.canvas.parentNode!.querySelector('div')
              if (!tooltipEl) {
                tooltipEl = document.createElement('div')
                tooltipEl.className = 'tooltip'

                const tooltipWrp = document.createElement('div')
                tooltipWrp.className = 'tooltip-wrp'
                tooltipEl.appendChild(tooltipWrp)

                chart.canvas.parentNode!.appendChild(tooltipEl)
              }
              // Hide if no tooltip
              if (tooltip.opacity === 0) {
                tooltipEl.style.opacity = '0'
                return
              }
              const [{ raw }] = tooltip.dataPoints
              const yValue = (raw as { y: number; x: number }).y
              const xValue = (raw as { y: number; x: number; time: number }).time
              const mainValue = `${yValue}`
              const subValue = `${format(new Date(xValue), 'dd.MM.yyyy')}`

              const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

              // Display, position, and set styles for font
              const innerHtml = `
                <div class='tooltip_container'>
                  <div class='tooltip_container-main-title'>${mainValue}</div>
                  <div class='tooltip_container-sub-title'>${subValue}</div>
                </div>
                <div class='tooltip_arrow_top'></div>
              `

              const tooltipWrp = tooltipEl.getElementsByClassName('tooltip-wrp')
              while (tooltipWrp[0].firstChild) {
                tooltipWrp[0].firstChild.remove()
              }
              const { scrollTop } = document.querySelector('.analytics-wrapper')!
              tooltipWrp[0].innerHTML = innerHtml

              tooltipEl.style.opacity = '1'
              tooltipEl.style.left = positionX + tooltip.caretX + 'px'
              tooltipEl.style.top = positionY + tooltip.caretY - scrollTop - 62 + 'px'
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              stepSize: 1,
              color: labelColor,
              callback: (value, index, values) => {
                const currentBubbleData = dataPositive.find(({ x }) => x === value)
                if (currentBubbleData) {
                  const { time } = currentBubbleData
                  return `${format(new Date(time), 'yyyy-MM-dd')}`
                }
                return null
              }
            }
          },
          y: {
            grid: {
              borderDash: [8, 8],
              color: gridColor,
              drawBorder: false
            },
            ticks: {
              color: labelColor,
              padding: 15
            }
          }
        }
      }
    })
    ctx.style.height = '230px'
    const chartWidth = Number.parseInt(ctx.style.width)
    chart.options.scales!.x!.min = -countPaddingDependingOfRadiusOfBubble(
      dataPositive,
      chartWidth,
      PADDINGS.LEFT_PADDING
    )!
    chart.options.scales!.x!.max =
      (dataPositive[dataPositive.length - 1]?dataPositive[dataPositive.length - 1].x:0)+
      countPaddingDependingOfRadiusOfBubble(dataPositive, chartWidth, PADDINGS.RIGTH_PADDING)!
    setMyChart(chart)
    return () => chart.destroy()
  }, [])

  useEffect(() => {
    if (myChart) {
      const ctx = document.getElementById(`${name}_${social}`)!
      const { dataPositive } = chartData
      myChart.data.datasets[0].data = dataPositive
      const chartWidth = Number.parseInt(ctx.style.width)
      myChart.options.scales!.x!.min = -countPaddingDependingOfRadiusOfBubble(
        dataPositive,
        chartWidth,
        PADDINGS.LEFT_PADDING
      )!
      myChart.options.scales!.x!.max =
       ( dataPositive[dataPositive.length - 1]?dataPositive[dataPositive.length - 1].x!:0) +
        countPaddingDependingOfRadiusOfBubble(dataPositive, chartWidth, PADDINGS.RIGTH_PADDING)!

       myChart.options.external = ({ chart, tooltip } = myChart.options.external) => {
          // Tooltip Element
          let tooltipEl = chart.canvas.parentNode!.querySelector('div')
          if (!tooltipEl) {
            tooltipEl = document.createElement('div')
            tooltipEl.className = 'tooltip'

            const tooltipWrp = document.createElement('div')
            tooltipWrp.className = 'tooltip-wrp'
            tooltipEl.appendChild(tooltipWrp)

            chart.canvas.parentNode!.appendChild(tooltipEl)
          }
          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = '0'
            return
          }
          const [{ raw }] = tooltip.dataPoints
          const yValue = (raw as { y: number; x: number }).y
          const xValue = (raw as { y: number; x: number; time: number }).time
          const mainValue = `${yValue}`
          const subValue = `${format(new Date(xValue), 'dd.MM.yyyy')}`

          const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

          // Display, position, and set styles for font
          const innerHtml = `
            <div class='tooltip_container'>
              <div class='tooltip_container-main-title'>${mainValue}</div>
              <div class='tooltip_container-sub-title'>${subValue}</div>
            </div>
            <div class='tooltip_arrow_top'></div>
          `

          const tooltipWrp = tooltipEl.getElementsByClassName('tooltip-wrp')
          while (tooltipWrp[0].firstChild) {
            tooltipWrp[0].firstChild.remove()
          }
          const { scrollTop } = document.querySelector('.analytics-wrapper')!
          tooltipWrp[0].innerHTML = innerHtml

          tooltipEl.style.opacity = '1'
          tooltipEl.style.left = positionX + tooltip.caretX + 'px'
          tooltipEl.style.top = positionY + tooltip.caretY - scrollTop - 62 + 'px'
        }

        myChart.options.scales.x.ticks.callback = (value:any, index:any, values:any) => {
                const currentBubbleData = dataPositive.find(({ x }:any) => x === value)
                if (currentBubbleData) {
                  const { time } = currentBubbleData
                  return `${format(new Date(time), 'yyyy-MM-dd')}`
                }
                return null
              }
       
      myChart.update()
    }
  }, [chartData])

  return (
    <>
      <canvas
        id={`${name}_${social}`}
        style={{ position: 'relative', height: '20vh', width: '80vw' }}
      ></canvas>
    </>
  )
}

export default BubbleChart
